import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, CheckCircle } from "lucide-react";
import { formatCurrency } from "./utils";

interface AiSuggestionsWidgetProps {
  pendingCount: number;
  totalValueCents: number;
  topSuggestions: Array<{
    _id: string;
    narrative: string;
    minutes: number;
    amountCents: number;
    matterId?: { title?: string; matterNumber?: string };
    aiConfidence?: number;
  }>;
}

const formatHours = (mins: number) => (mins / 60).toFixed(1);

const getConfidenceColor = (confidence?: number): string => {
  if (confidence == null) return "bg-slate-300";
  if (confidence >= 0.8) return "bg-emerald-500";
  if (confidence >= 0.5) return "bg-amber-500";
  return "bg-red-500";
};

const getConfidenceLabel = (confidence?: number): string => {
  if (confidence == null) return "Unknown";
  if (confidence >= 0.8) return "High confidence";
  if (confidence >= 0.5) return "Medium confidence";
  return "Low confidence";
};

const AiSuggestionsWidget: React.FC<AiSuggestionsWidgetProps> = ({
  pendingCount,
  totalValueCents,
  topSuggestions,
}) => {
  const navigate = useNavigate();
  const displaySuggestions = topSuggestions.slice(0, 5);

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles
            className="h-5 w-5 text-slate-700"
            aria-hidden="true"
          />
          <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
            AI Time Suggestions
          </h2>
        </div>
        {pendingCount > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {pendingCount}
          </span>
        )}
      </div>

      {/* Body */}
      {pendingCount === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center mb-3"
            style={{ backgroundColor: "var(--color-primary-light)" }}
          >
            <CheckCircle
              className="h-6 w-6"
              style={{ color: "var(--color-accent)" }}
            />
          </div>
          <p className="font-medium text-slate-500 text-sm">All caught up!</p>
          <p className="text-xs text-slate-400">
            No pending AI suggestions to review
          </p>
        </div>
      ) : (
        <>
          {/* Total value summary */}
          <div className="mb-4 px-3 py-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500">
              Total pending value
            </p>
            <p
              className="text-sm font-bold"
              style={{ color: "#1F2937" }}
            >
              {formatCurrency(totalValueCents)}
            </p>
          </div>

          {/* Suggestion rows */}
          <div className="space-y-2">
            {displaySuggestions.map((suggestion, index) => (
              <motion.div
                key={suggestion._id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05, duration: 0.2 }}
                className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                {/* Confidence dot */}
                <span
                  className={`h-2 w-2 rounded-full flex-shrink-0 ${getConfidenceColor(
                    suggestion.aiConfidence
                  )}`}
                  title={getConfidenceLabel(suggestion.aiConfidence)}
                  aria-label={getConfidenceLabel(suggestion.aiConfidence)}
                />

                {/* Matter + Narrative */}
                <div className="flex-1 min-w-0">
                  {suggestion.matterId?.matterNumber && (
                    <span
                      className="text-xs font-semibold mr-1.5"
                      style={{ color: "var(--color-accent)" }}
                    >
                      {suggestion.matterId.matterNumber}
                    </span>
                  )}
                  <span className="text-xs text-slate-600 truncate block">
                    {suggestion.narrative}
                  </span>
                </div>

                {/* Hours */}
                <span
                  className="text-xs font-medium text-slate-700 flex-shrink-0"
                >
                  {formatHours(suggestion.minutes)}h
                </span>

                {/* Amount */}
                <span
                  className="text-xs font-bold flex-shrink-0"
                  style={{ color: "#1F2937" }}
                >
                  {formatCurrency(suggestion.amountCents)}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="flex items-center gap-1.5 text-sm font-semibold transition-colors"
              style={{ color: "var(--color-primary)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--color-primary)";
              }}
            >
              Review All
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </motion.section>
  );
};

export default AiSuggestionsWidget;
